import { Input, Select } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useAuth } from '@/contexts/AuthContext';
import { CreditCard, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import {
  localBusinessDate,
  saleCollectedAmount,
  salePaymentDraftSchema,
  type SalePaymentDraft,
} from '@klothiq/validation';

export interface RecordedSalePayment {
  id: string;
  amount: number | string;
  account_id: string;
  payment_method: string;
  payment_date: string;
  payment_type: string;
  reference_number?: string | null;
  notes?: string | null;
  is_void: boolean;
  updated_at?: string;
}
export interface PaymentCorrection extends SalePaymentDraft {
  payment_id: string;
  expected_updated_at?: string;
}
export interface SalePaymentChanges {
  enabled: boolean;
  partial: boolean;
  payment: SalePaymentDraft;
  edits: PaymentCorrection[];
}
export function emptyPaymentChanges(timezone?: string): SalePaymentChanges {
  return {
    enabled: false,
    partial: false,
    edits: [],
    payment: {
      amount: 0,
      account_id: '',
      payment_method: 'cash',
      payment_date: localBusinessDate(timezone),
      reference_number: '',
      notes: '',
    },
  };
}

const inputClass =
  'h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';
const rupees = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

export function PaymentFields({
  value,
  onChange,
  accounts,
  prefix,
  amountLocked = false,
  reason = false,
  disabled = false,
}: {
  value: SalePaymentDraft;
  onChange: (next: SalePaymentDraft) => void;
  accounts: Array<{ id: string; name: string }>;
  prefix: string;
  amountLocked?: boolean;
  reason?: boolean;
  disabled?: boolean;
}) {
  const update = (patch: Partial<SalePaymentDraft>) => onChange({ ...value, ...patch });
  const [touched, setTouched] = useState<Set<keyof SalePaymentDraft>>(new Set());
  const checked = salePaymentDraftSchema.safeParse(value);
  const fieldError = (field: keyof SalePaymentDraft) =>
    touched.has(field) && !checked.success
      ? checked.error.issues.find((issue) => issue.path[0] === field)?.message
      : undefined;
  const inputA11y = (field: keyof SalePaymentDraft) => ({
    'aria-invalid': !!fieldError(field),
    'aria-describedby': fieldError(field) ? `${prefix}-${field}-error` : undefined,
    onBlur: () => setTouched((previous) => new Set(previous).add(field)),
  });
  const feedback = (field: keyof SalePaymentDraft) =>
    fieldError(field) ? (
      <p id={`${prefix}-${field}-error`} className="mt-1 text-xs text-destructive">
        {fieldError(field)}
      </p>
    ) : null;
  return (
    <fieldset disabled={disabled} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label htmlFor={`${prefix}-amount`} className="mb-1.5 block text-sm font-medium">
          Amount (₹)
        </label>
        <Input
          id={`${prefix}-amount`}
          {...inputA11y('amount')}
          className={inputClass}
          type="number"
          min="0.01"
          step="0.01"
          value={value.amount || ''}
          readOnly={amountLocked}
          onChange={(e) => update({ amount: Number(e.target.value) })}
          helpLabel="Amount (₹)"
        />
        {feedback('amount')}
      </div>
      <div>
        <label htmlFor={`${prefix}-method`} className="mb-1.5 block text-sm font-medium">
          Payment method
        </label>
        <Select
          id={`${prefix}-method`}
          {...inputA11y('payment_method')}
          className={inputClass}
          value={value.payment_method}
          onChange={(e) => update({ payment_method: e.target.value as SalePaymentDraft['payment_method'] })}
          helpLabel="Payment method"
        >
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="bank_transfer">Bank transfer</option>
          <option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </Select>
        {feedback('payment_method')}
      </div>
      <div>
        <label htmlFor={`${prefix}-account`} className="mb-1.5 block text-sm font-medium">
          Receiving account
        </label>
        <Select
          id={`${prefix}-account`}
          {...inputA11y('account_id')}
          className={inputClass}
          value={value.account_id}
          onChange={(e) => update({ account_id: e.target.value })}
          helpLabel="Receiving account"
        >
          <option value="">Select an account</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        {feedback('account_id')}
      </div>
      <div>
        <label htmlFor={`${prefix}-date`} className="mb-1.5 block text-sm font-medium">
          Payment date
        </label>
        <DatePicker
          disabled={disabled}
          clearable={false}
          aria-label="Payment date"
          id={`${prefix}-date`}
          {...inputA11y('payment_date')}
          value={value.payment_date}
          onChange={(nextDate) => update({ payment_date: nextDate ?? '' })}
        />
        {feedback('payment_date')}
      </div>
      <div>
        <label htmlFor={`${prefix}-ref`} className="mb-1.5 block text-sm font-medium">
          Transaction reference (optional)
        </label>
        <Input
          id={`${prefix}-ref`}
          {...inputA11y('reference_number')}
          className={inputClass}
          maxLength={120}
          value={value.reference_number ?? ''}
          onChange={(e) => update({ reference_number: e.target.value })}
          helpLabel="Transaction reference (optional)"
        />
        {feedback('reference_number')}
      </div>
      <div>
        <label htmlFor={`${prefix}-notes`} className="mb-1.5 block text-sm font-medium">
          Payment notes (optional)
        </label>
        <Input
          id={`${prefix}-notes`}
          {...inputA11y('notes')}
          className={inputClass}
          maxLength={1000}
          value={value.notes ?? ''}
          onChange={(e) => update({ notes: e.target.value })}
          helpLabel="Payment notes (optional)"
        />
        {feedback('notes')}
      </div>
      {reason && (
        <div className="sm:col-span-2">
          <label htmlFor={`${prefix}-reason`} className="mb-1.5 block text-sm font-medium">
            Correction reason
          </label>
          <Input
            id={`${prefix}-reason`}
            {...inputA11y('reason')}
            className={inputClass}
            maxLength={500}
            value={value.reason ?? ''}
            placeholder="Explain an amount or account correction"
            onChange={(e) => update({ reason: e.target.value })}
            helpLabel="Correction reason"
          />
          {feedback('reason')}
        </div>
      )}
    </fieldset>
  );
}

export function projectedCollected(payments: RecordedSalePayment[], edits: PaymentCorrection[]) {
  return saleCollectedAmount(payments, edits);
}

export default function SalePaymentsEditor({
  value,
  onChange,
  payments,
  accounts,
  total,
  eligible,
  isEditing,
  disabled,
  error,
  onVoid,
}: {
  value: SalePaymentChanges;
  onChange: (value: SalePaymentChanges) => void;
  payments: RecordedSalePayment[];
  accounts: Array<{ id: string; name: string }>;
  total: number;
  eligible: boolean;
  isEditing: boolean;
  disabled: boolean;
  error?: string | null;
  onVoid?: (payment: RecordedSalePayment, reason: string) => Promise<void>;
}) {
  const [voiding, setVoiding] = useState<{ id: string; reason: string } | null>(null);
  const { can } = useAuth();
  const collected = projectedCollected(payments, value.edits);
  const remaining = Math.round((total - collected) * 100) / 100;
  const update = (patch: Partial<SalePaymentChanges>) => onChange({ ...value, ...patch });
  return (
    <section
      aria-labelledby="sale-payment-heading"
      className="obsidian-surface space-y-4 rounded-lg border border-border bg-card p-4 shadow-sm sm:p-5"
    >
      <div>
        <h2 id="sale-payment-heading" className="flex items-center gap-2 text-base font-semibold">
          <CreditCard className="h-4 w-4" /> Payments
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Review recorded payments, correct an entry, or collect the remaining balance.
        </p>
      </div>
      <dl className="grid grid-cols-3 gap-2 rounded-lg bg-muted/30 p-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Sale total</dt>
          <dd className="mt-1 font-semibold break-words">{rupees(total)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">
            {value.edits.length ? 'Collected after save' : 'Collected'}
          </dt>
          <dd className="mt-1 font-semibold break-words">{rupees(collected)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Remaining</dt>
          <dd
            className={`mt-1 font-semibold break-words ${remaining < 0 ? 'text-destructive' : ''}`}
          >
            {rupees(remaining)}
          </dd>
        </div>
      </dl>
      {payments.length === 0 && (
        <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
      )}
      {payments.map((p) => {
        const edit = value.edits.find((e) => e.payment_id === p.id);
        return (
          <div key={p.id} className="space-y-3 rounded-lg border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p
                  className={`font-semibold ${p.is_void ? 'line-through text-muted-foreground' : ''}`}
                >
                  {p.payment_type === 'refund' ? 'Refund · ' : ''}
                  {rupees(Number(p.amount))}
                  {p.is_void && ' · Voided'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {p.payment_date} · {p.payment_method.replace('_', ' ')} ·{' '}
                  {accounts.find((a) => a.id === p.account_id)?.name ?? 'Recorded account'}
                </p>
                {p.reference_number && (
                  <p className="mt-1 break-words text-xs text-muted-foreground">
                    Reference: {p.reference_number}
                  </p>
                )}
                {p.notes && (
                  <p className="mt-1 break-words text-xs text-muted-foreground">{p.notes}</p>
                )}
                {edit && (
                  <p className="mt-1 text-xs font-medium text-primary">
                    After save: {rupees(edit.amount)}
                  </p>
                )}
              </div>
              {!p.is_void && can('sales.correct') && (p.payment_type !== 'refund' || can('sales.refund')) && (
                <button
                  type="button"
                  disabled={disabled}
                  className="inline-flex min-h-10 items-center gap-2 rounded-md border px-3 text-sm hover:bg-accent disabled:opacity-50"
                  onClick={() => {
                    if (edit) update({ edits: value.edits.filter((e) => e.payment_id !== p.id) });
                    else
                      update({
                        edits: [
                          ...value.edits,
                          {
                            payment_id: p.id,
                            expected_updated_at: p.updated_at,
                            amount: Number(p.amount),
                            account_id: p.account_id,
                            payment_method: p.payment_method as SalePaymentDraft['payment_method'],
                            payment_date: p.payment_date,
                            reference_number: p.reference_number ?? '',
                            notes: p.notes ?? '',
                            reason: '',
                          },
                        ],
                      });
                  }}
                >
                  {edit ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  {edit ? 'Cancel correction' : 'Edit this payment'}
                </button>
              )}
            </div>
            {!p.is_void && onVoid && can('sales.correct') && can('owner') && (
              <button
                type="button"
                disabled={disabled}
                className="min-h-10 text-sm text-destructive underline disabled:opacity-50"
                onClick={() => setVoiding(voiding?.id === p.id ? null : { id: p.id, reason: '' })}
              >
                Void this payment
              </button>
            )}
            {voiding?.id === p.id && (
              <div className="space-y-2 rounded-md bg-destructive/5 p-3">
                <label htmlFor={`void-${p.id}`} className="block text-sm font-medium">
                  Void reason
                </label>
                <Input
                  id={`void-${p.id}`}
                  disabled={disabled}
                  className={inputClass}
                  maxLength={500}
                  value={voiding.reason}
                  onChange={(e) => setVoiding({ id: p.id, reason: e.target.value })}
                  helpLabel="Void reason"
                />
                <p className="text-xs text-muted-foreground">
                  The entry stays in history and its money is reversed. Unsaved sale changes will be
                  discarded after voiding.
                </p>
                <button
                  type="button"
                  disabled={disabled || voiding.reason.trim().length < 3}
                  className="min-h-11 rounded-md bg-destructive px-3 text-sm text-destructive-foreground disabled:opacity-50"
                  onClick={async () => {
                    await onVoid?.(p, voiding.reason.trim());
                  }}
                >
                  Confirm void
                </button>
              </div>
            )}
            {edit && (
              <>
                <p className="text-xs text-muted-foreground">
                  This corrects the selected entry when you save the sale.
                </p>
                <PaymentFields
                  value={edit}
                  prefix={`payment-${p.id}`}
                  accounts={accounts}
                  reason
                  disabled={disabled}
                  onChange={(next) =>
                    update({
                      edits: value.edits.map((e) =>
                        e.payment_id === p.id ? { ...e, ...next } : e,
                      ),
                    })
                  }
                />
              </>
            )}
          </div>
        );
      })}
      <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm font-medium">
        <Input
          type="checkbox"
          className="h-5 w-5 accent-primary"
          checked={value.enabled}
          disabled={disabled || !can('sales.collect') || (!eligible && !value.enabled)}
          onChange={(e) => update({ enabled: e.target.checked })}
          helpLabel="Record payment"
        />
        {isEditing && payments.some((p) => !p.is_void) ? (
          <>
            <Plus className="h-4 w-4" /> Add another payment
          </>
        ) : (
          'Payment collected now'
        )}
      </label>
      {!eligible && (
        <p className="text-sm text-muted-foreground">
          New collection is unavailable in this fulfillment status. Existing entries can still be
          corrected.
        </p>
      )}
      {value.enabled && eligible && (
        <div className="space-y-3 rounded-lg bg-muted/20 p-3">
          <label className="flex min-h-10 items-center gap-3 text-sm">
            <Input
              type="checkbox"
              className="h-5 w-5 accent-primary"
              checked={value.partial}
              disabled={disabled}
              onChange={(e) => update({ partial: e.target.checked })}
              helpLabel="Enable partial payment"
            />{' '}
            Enable partial payment
          </label>
          <p className="text-xs text-muted-foreground">
            {value.partial
              ? 'Enter this installment only. The remainder stays unpaid.'
              : `Collect the full remaining balance: ${rupees(Math.max(0, remaining))}.`}
          </p>
          <PaymentFields
            value={{
              ...value.payment,
              amount: value.partial ? value.payment.amount : Math.max(0, remaining),
            }}
            amountLocked={!value.partial}
            prefix="new-payment"
            accounts={accounts}
            disabled={disabled}
            onChange={(payment) => update({ payment })}
          />
          {payments.some((p) => !p.is_void) && (
            <p className="text-xs text-muted-foreground">
              This adds a new collection. To correct money already entered, use “Edit this payment”
              above.
            </p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      )}
    </section>
  );
}
